import { useState, useEffect } from 'react'
import { B2BvsB2CWidget } from './B2BvsB2CWidget'
import { TrendingUp, Users, DollarSign, Globe, Activity, Download } from 'lucide-react'
import { authService } from '../../services/auth'
import { fetchAuthSession } from 'aws-amplify/auth'
import { GeographicWidgets } from './GeographicWidgets'
import { MarketingAttribution } from './MarketingAttribution'
import { Chart, registerables } from 'chart.js'

Chart.register(...registerables)

interface BusinessMetrics {
  revenue: {
    total: number
    totalINR: string
    calculation: {
      completedTransactions: number
      pricePerUpgrade: number
      formula: string
      breakdown: string
    }
    transactions: number
    byCountry: Record<string, number>
  }
  users: {
    total: number
    calculation: {
      anonymousUsers: number
      registeredUsers: number
      proUsers: number
      breakdown: string
    }
    byRole: Record<string, number>
    byCountry: Record<string, number>
  }
  conversions: {
    rate: number
    calculation: {
      formula: string
      breakdown: string
    }
    registeredToPro: number
    totalRegistered: number
  }
  transactions: {
    total: number
    completed: number
    failed: number
    pending: number
    byStatus: Record<string, number>
  }
  dailyRevenue: Record<string, number>
  conversionFunnel: {
    registered: number
    loggedIn: number
    upgraded: number
    registrationRate: number
    loginRate: number
    conversionRate: number
  }
  recentTransactions: Array<{
    id: string
    amount: number
    currency: string
    status: string
    gateway: string
    userId: string
    userEmail?: string
    userName?: string
    solutionName?: string
    billingCountry?: string
    date: string
  }>
  businessInsights: Array<{
    type: string
    text: string
  }>
}

interface GeographicData {
  sessions: {
    total: number
    byCountry: Record<string, number>
    byCity: Record<string, number>
    byTimezone: Record<string, number>
    byDevice: Record<string, number>
  }
  revenue: {
    byCountry: Record<string, number>
    transactionsByCountry: Record<string, number>
  }
  users: {
    total: number
    byCountry: Record<string, number>
    byRole: Record<string, number>
  }
}

interface MsmeKpis {
  period: string
  revenue: {
    mrr: number
    arr: number
    arpu: number
  }
  customers: {
    activeUsers: number
    churnRate: number
    conversionRate: number
  }
  growth: {
    mrrGrowth: string | number
    userGrowth: string | number
  }
}

export function AnalyticsDashboard() {
  const [businessMetrics, setBusinessMetrics] = useState<BusinessMetrics | null>(null)
  const [geographicData, setGeographicData] = useState<GeographicData | null>(null)
  const [msmeKpis, setMsmeKpis] = useState<MsmeKpis | null>(null)
  const [b2bB2cData, setB2bB2cData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Export functionality
  const exportToCSV = () => {
    if (!businessMetrics) return

    const csvData = [
      ['Metric', 'Value'],
      ['Total Revenue', businessMetrics.revenue.totalINR],
      ['Revenue Calculation', businessMetrics.revenue.calculation.formula],
      ['Total Users', businessMetrics.users.total],
      ['User Breakdown', businessMetrics.users.calculation.breakdown],
      ['Total Transactions', businessMetrics.transactions.completed],
      ['Transaction Breakdown', `${businessMetrics.transactions.completed} completed, ${businessMetrics.transactions.failed} failed, ${businessMetrics.transactions.pending} pending`],
      ['Conversion Rate', `${businessMetrics.conversions.rate}%`],
      ['Conversion Calculation', businessMetrics.conversions.calculation.formula]
    ]

    const csvContent = csvData.map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `analytics-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  useEffect(() => {
    loadAnalyticsData()
  }, [])

  // Bulletproof chart rendering with retry logic
  useEffect(() => {
    if (loading) {
      console.log('⏳ Still loading, waiting...')
      return
    }

    if (!businessMetrics?.dailyRevenue) {
      console.log('❌ No businessMetrics or dailyRevenue')
      return
    }

    if (Object.keys(businessMetrics.dailyRevenue).length === 0) {
      console.log('❌ dailyRevenue is empty')
      return
    }

    console.log('✅ Data ready, attempting to render chart...')
    
    // Retry logic: try up to 10 times with 100ms delay
    let attempts = 0
    const maxAttempts = 10
    
    const tryRender = () => {
      attempts++
      const canvas = document.getElementById('adminRevenueChart') as HTMLCanvasElement
      
      if (canvas) {
        console.log(`✅ Canvas found on attempt ${attempts}, rendering chart`)
        renderRevenueChart()
      } else if (attempts < maxAttempts) {
        console.log(`⏳ Canvas not found, retry ${attempts}/${maxAttempts}`)
        setTimeout(tryRender, 100)
      } else {
        console.error(`❌ Canvas not found after ${maxAttempts} attempts`)
      }
    }

    // Start trying after a small delay
    setTimeout(tryRender, 50)

    // Cleanup function
    return () => {
      const canvas = document.getElementById('adminRevenueChart') as HTMLCanvasElement
      if (canvas) {
        const existingChart = Chart.getChart(canvas)
        if (existingChart) {
          existingChart.destroy()
        }
      }
    }
  }, [businessMetrics, loading])

  const renderRevenueChart = () => {
    const canvas = document.getElementById('adminRevenueChart') as HTMLCanvasElement
    if (!canvas) {
      console.error('Canvas element not found')
      return
    }
    if (!businessMetrics?.dailyRevenue) {
      console.error('No businessMetrics.dailyRevenue')
      return
    }

    const dailyRevenue = businessMetrics.dailyRevenue
    const sortedDates = Object.keys(dailyRevenue).sort()
    
    if (sortedDates.length === 0) {
      console.error('dailyRevenue is empty object')
      return
    }

    console.log('Chart data:', { sortedDates, dailyRevenue })

    // Clear any existing chart
    const existingChart = Chart.getChart(canvas)
    if (existingChart) {
      existingChart.destroy()
    }

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      console.error('Failed to get canvas context')
      return
    }

    const labels = sortedDates.map(date => {
      const d = new Date(date)
      return `${d.getDate()}/${d.getMonth() + 1}`
    })
    const values = sortedDates.map(date => dailyRevenue[date])

    console.log('Creating chart with:', { labels, values })

    new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: 'Daily Revenue (₹)',
          data: values,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true,
          pointBackgroundColor: '#10b981',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        devicePixelRatio: 2,
        scales: { 
          y: { 
            beginAtZero: true,
            ticks: {
              font: { size: 14 },
              color: '#1f2937',
              callback: function(value: any) {
                return '₹' + value.toLocaleString()
              }
            },
            grid: { color: 'rgba(0, 0, 0, 0.08)' }
          },
          x: {
            ticks: {
              font: { size: 13 },
              color: '#1f2937',
              maxTicksLimit: 10
            },
            grid: { color: 'rgba(0, 0, 0, 0.05)' }
          }
        },
        plugins: {
          legend: {
            labels: {
              font: { size: 13 },
              color: '#1f2937',
              padding: 15
            }
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleFont: { size: 13 },
            bodyFont: { size: 12 },
            padding: 10,
            callbacks: {
              title: function(context: any) {
                const dateIndex = context[0].dataIndex
                const date = sortedDates[dateIndex]
                return new Date(date).toLocaleDateString()
              },
              label: function(context: any) {
                return 'Revenue: ₹' + context.parsed.y
              }
            }
          }
        }
      }
    })
    
    console.log('Chart created successfully')
  }

  const getAuthToken = async () => {
    let token = authService.getToken()
    
    if (!token) {
      try {
        const session = await fetchAuthSession()
        token = session.tokens?.idToken?.toString() || null
        if (token) {
          authService.setToken(token)
        }
      } catch (error) {
        console.error('Failed to get session token:', error)
      }
    }
    
    return token
  }

  const loadAnalyticsData = async () => {
    console.log('🔄 Loading analytics data...')
    try {
      setLoading(true)
      setError(null)
      
      const token = await getAuthToken()
      if (!token) {
        console.error('❌ No auth token')
        setError('Authentication required')
        return
      }

      const headers = { 'Authorization': `Bearer ${token}` }
      const baseUrl = (import.meta as any).env.VITE_API_URL.replace(/\/$/, '')
      console.log('📡 Fetching from:', baseUrl)

      // Load all analytics data in parallel
      const [businessRes, geographicRes, msmeRes, b2bB2cRes] = await Promise.all([
        fetch(`${baseUrl}/api/analytics/business-metrics`, { headers }),
        fetch(`${baseUrl}/api/analytics/geographic`, { headers }),
        fetch(`${baseUrl}/api/analytics/msme-kpis`, { headers }),
        fetch(`${baseUrl}/api/analytics/b2b-b2c`, { headers })
      ])

      if (businessRes.ok) {
        const data = await businessRes.json()
        console.log('✅ Business metrics loaded:', data)
        setBusinessMetrics(data)
      } else {
        console.error('❌ Failed to load business metrics:', businessRes.status)
      }

      if (geographicRes.ok) {
        const data = await geographicRes.json()
        setGeographicData(data)
      } else {
        console.error('Failed to load geographic data:', geographicRes.status)
      }

      // Usage data removed - not needed

      if (msmeRes.ok) {
        const data = await msmeRes.json()
        setMsmeKpis(data)
      } else {
        console.error('Failed to load MSME KPIs:', msmeRes.status)
      }

      if (b2bB2cRes.ok) {
        const data = await b2bB2cRes.json()
        console.log('✅ B2B/B2C data loaded:', data)
        setB2bB2cData(data)
      } else {
        console.error('❌ Failed to load B2B/B2C data:', b2bB2cRes.status)
      }

    } catch (error) {
      console.error('Failed to load analytics data:', error)
      setError('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white p-6 rounded-lg shadow">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button 
            onClick={loadAnalyticsData}
            className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
        <div className="flex space-x-3">
          <button
            onClick={exportToCSV}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
          <button
            onClick={loadAnalyticsData}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center"
          >
            <Activity className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Analytics Presets */}
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                {businessMetrics ? businessMetrics.revenue.totalINR : '₹0'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {businessMetrics ? businessMetrics.revenue.calculation.breakdown : ''}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">
                {businessMetrics ? businessMetrics.users.total.toLocaleString() : '0'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {businessMetrics ? businessMetrics.users.calculation.breakdown : ''}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {businessMetrics ? `${businessMetrics.conversions.rate}%` : '0%'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {businessMetrics ? businessMetrics.conversions.calculation.breakdown : ''}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Activity className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-900">
                {businessMetrics ? businessMetrics.transactions.completed.toLocaleString() : '0'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {businessMetrics ? `${businessMetrics.transactions.completed} completed payments` : ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Conversion Funnel */}
      {businessMetrics?.conversionFunnel && (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-semibold mb-6 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            🔄 Conversion Funnel
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-blue-50 rounded-lg border-2 border-blue-200">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                {businessMetrics.conversionFunnel.registered.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 mb-1">Registered Users</div>
              <div className="text-xs text-blue-600 font-medium">
                {businessMetrics.conversionFunnel.registrationRate.toFixed(1)}% of total
              </div>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-lg border-2 border-purple-200">
              <div className="text-4xl font-bold text-purple-600 mb-2">
                {businessMetrics.conversionFunnel.loggedIn.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 mb-1">Active Users</div>
              <div className="text-xs text-purple-600 font-medium">
                {businessMetrics.conversionFunnel.loginRate.toFixed(1)}% login rate
              </div>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-lg border-2 border-green-200">
              <div className="text-4xl font-bold text-green-600 mb-2">
                {businessMetrics.conversionFunnel.upgraded.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 mb-1">Pro Upgrades</div>
              <div className="text-xs text-green-600 font-medium">
                {businessMetrics.conversionFunnel.conversionRate.toFixed(1)}% conversion
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MSME KPIs */}
      {msmeKpis && (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-semibold mb-6 flex items-center">
            <DollarSign className="h-5 w-5 mr-2" />
            📊 Business KPIs (MSME Metrics)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Monthly Recurring Revenue</div>
              <div className="text-2xl font-bold text-green-700">₹{msmeKpis.revenue.mrr.toLocaleString()}</div>
              <div className="text-xs text-gray-500 mt-1">ARR: ₹{msmeKpis.revenue.arr.toLocaleString()}</div>
            </div>
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Avg Revenue Per User (ARPU)</div>
              <div className="text-2xl font-bold text-blue-700">₹{msmeKpis.revenue.arpu.toFixed(0)}</div>
              <div className="text-xs text-gray-500 mt-1">Annual (ARR ÷ Total Users)</div>
            </div>
            <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Active Users</div>
              <div className="text-2xl font-bold text-purple-700">{msmeKpis.customers.activeUsers}</div>
              <div className="text-xs text-gray-500 mt-1">Conversion: {msmeKpis.customers.conversionRate}%</div>
            </div>
            <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Churn Rate</div>
              <div className="text-2xl font-bold text-orange-700">{msmeKpis.customers.churnRate}%</div>
              <div className="text-xs text-gray-500 mt-1">MRR Growth: {msmeKpis.growth.mrrGrowth}</div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Status & User Role Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            📊 Transaction Status Breakdown
          </h3>
          <div className="space-y-3">
            {businessMetrics && businessMetrics.transactions && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Transactions</span>
                  <span className="font-semibold">{businessMetrics.transactions.total}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-green-600">✅ Completed</span>
                  <span className="font-semibold text-green-600">{businessMetrics.transactions.completed}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-red-600">❌ Failed</span>
                  <span className="font-semibold text-red-600">{businessMetrics.transactions.failed}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-yellow-600">⏳ Pending</span>
                  <span className="font-semibold text-yellow-600">{businessMetrics.transactions.pending}</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2" />
            👥 User Role Distribution
          </h3>
          <div className="space-y-3">
            {businessMetrics && Object.entries(businessMetrics.users.byRole).map(([role, count]) => (
              <div key={role} className="flex justify-between items-center">
                <span className="text-gray-600 capitalize">{role}</span>
                <span className="font-semibold">{count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Geographic Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Globe className="h-5 w-5 mr-2" />
            Revenue by Country
          </h3>
          <div className="space-y-3">
            {businessMetrics && Object.entries(businessMetrics.revenue.byCountry).map(([country, revenue]) => (
              <div key={country} className="flex justify-between items-center">
                <span className="text-gray-600">{country}</span>
                <span className="font-semibold">{formatCurrency(revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Daily Revenue Trend */}
      <div className="grid grid-cols-1 gap-8 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            💰 Daily Revenue Trend
          </h3>
          <div style={{ height: '350px', position: 'relative' }}>
            <canvas id="adminRevenueChart" style={{ display: 'block', width: '100%', height: '100%' }}></canvas>
          </div>
        </div>
      </div>

      {/* Marketing Attribution */}
      <div className="mb-8">
        <MarketingAttribution />
      </div>

      {/* Recent Transactions */}
      <div className="grid grid-cols-1 gap-8 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <DollarSign className="h-5 w-5 mr-2" />
            💳 Recent Transactions
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Solution</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gateway</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {businessMetrics && businessMetrics.recentTransactions && businessMetrics.recentTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{transaction.userName || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{transaction.userEmail || transaction.userId}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{transaction.solutionName || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">₹{transaction.amount.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{transaction.billingCountry || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{transaction.date}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{transaction.gateway}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                        transaction.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {transaction.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Geographic Dashboard Widgets */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <Globe className="h-6 w-6 mr-2" />
          Geographic Analytics
        </h2>
        <GeographicWidgets 
          data={geographicData} 
          loading={loading} 
        />
      </div>

      {/* B2B vs B2C Analytics */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4 flex items-center">
          <Activity className="h-6 w-6 mr-2" />
          B2B vs B2C Analytics
        </h2>
        <B2BvsB2CWidget data={b2bB2cData} loading={loading} />
      </div>
    </div>
  )
}
